import { useEffect, useRef, useState } from 'react';

const serviceCategories = [
  {
    icon: 'ri-dna-line',
    title: 'Bioinformática y análisis ómico',
    color: 'teal',
    items: [
      'RNA-seq',
      'ChIP-seq',
      'Genómica y transcriptómica',
      'Variantes genéticas',
      'Integración multi-ómica',
      'Visualización de datos biológicos',
    ],
  },
  {
    icon: 'ri-bar-chart-grouped-line',
    title: 'Ciencia de datos aplicada',
    color: 'emerald',
    items: [
      'Estadística avanzada',
      'Modelos predictivos',
      'Machine Learning para biología',
      'Curación de bases de datos',
      'Dashboards científicos',
      'Reportes reproducibles',
    ],
  },
  {
    icon: 'ri-node-tree',
    title: 'Biología de sistemas',
    color: 'cyan',
    items: [
      'Redes de regulación genética',
      'Modelado computacional',
      'Simulación de procesos biológicos',
      'Integración de evidencia experimental',
    ],
  },
  {
    icon: 'ri-settings-3-line',
    title: 'Infraestructura científica',
    color: 'teal',
    items: [
      'Pipelines reproducibles',
      'Automatización de flujos de trabajo',
      'Herramientas CLI',
      'Nextflow y workflows científicos',
      'Documentación técnica',
    ],
  },
  {
    icon: 'ri-plant-line',
    title: 'Biodiversidad y conservación',
    color: 'emerald',
    items: [
      'Genómica de conservación',
      'Monitoreo molecular',
      'Diversidad genética',
      'Análisis ecológicos y ambientales',
      'Estudios de impacto ambiental',
    ],
  },
];

const colorMap: Record<string, { bg: string; text: string; border: string; hover: string }> = {
  teal: { bg: 'bg-biodata-teal/10', text: 'text-biodata-teal', border: 'border-biodata-teal/20', hover: 'group-hover:bg-biodata-teal/20' },
  emerald: { bg: 'bg-biodata-emerald/10', text: 'text-biodata-emerald', border: 'border-biodata-emerald/20', hover: 'group-hover:bg-biodata-emerald/20' },
  cyan: { bg: 'bg-biodata-cyan/10', text: 'text-biodata-cyan', border: 'border-biodata-cyan/20', hover: 'group-hover:bg-biodata-cyan/20' },
};

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setInView(true);
    }, { threshold });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function ServicesSection() {
  const { ref: sectionRef, inView } = useInView(0.1);

  return (
    <section
      id="services"
      ref={sectionRef}
      className="relative py-20 md:py-32 bg-biodata-surface overflow-hidden"
    >
      {/* Subtle Grid Pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `linear-gradient(rgba(13,148,136,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(13,148,136,0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-biodata-emerald/10 border border-biodata-emerald/20 mb-4">
            <span className="text-xs text-biodata-emerald font-medium uppercase tracking-wider">Servicios</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            Soluciones de <span className="text-gradient">excelencia científica</span>
          </h2>
          <p className="text-biodata-muted text-base md:text-lg leading-relaxed">
            Desde el análisis genómico hasta la inteligencia artificial aplicada, 
            ofrecemos un portafolio integral de servicios para la investigación moderna.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {serviceCategories.map((category, index) => {
            const c = colorMap[category.color];
            return (
              <div
                key={category.title}
                className={`group glass-card rounded-2xl p-6 md:p-8 hover:border-biodata-teal/30 transition-all duration-500 cursor-pointer ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div
                  className={`w-14 h-14 rounded-xl ${c.bg} ${c.hover} border ${c.border} flex items-center justify-center mb-6 transition-all duration-300`}
                >
                  <i className={`${category.icon} text-2xl ${c.text}`} />
                </div>

                <h3 className="text-lg font-display font-semibold text-white mb-4 group-hover:text-biodata-teal transition-colors">
                  {category.title}
                </h3>

                <ul className="space-y-2">
                  {category.items.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-sm text-biodata-muted">
                      <span className={`w-1.5 h-1.5 rounded-full ${c.bg.replace('/10', '/40')} ${c.text}`} />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-16 text-center transition-all duration-700 delay-500 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <p className="text-biodata-muted mb-4">
            ¿Necesitas una solución personalizada para tu proyecto?
          </p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-biodata-teal text-white font-medium hover:bg-biodata-teal/90 transition-all duration-300 shadow-lg shadow-biodata-teal/25 whitespace-nowrap"
          >
            <span>Hablemos de tu proyecto</span>
            <i className="ri-arrow-right-line" />
          </a>
        </div>
      </div>
    </section>
  );
}